import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDateTime } from '../api';
import { ROLLEN_LABEL, useAuth } from '../auth';
import { Badge, Card, Dialog, Field, Loading, Notice } from '../components/ui';
import type { Rolle } from '../types';

interface Konto {
  id: string;
  email: string;
  name: string;
  rolle: Rolle;
  aktiv: boolean;
  angelegt_am: string;
}

export function BenutzerPage() {
  const { user } = useAuth();
  const [konten, setKonten] = useState<Konto[] | null>(null);
  const [bearbeite, setBearbeite] = useState<Konto | 'neu' | null>(null);
  const [form, setForm] = useState({ email: '', name: '', rolle: 'verkauf' as Rolle, passwort: '', aktiv: true });
  const [fehler, setFehler] = useState('');

  const laden = useCallback(() => {
    void api<Konto[]>('/api/benutzer').then(setKonten);
  }, []);
  useEffect(laden, [laden]);

  const oeffne = (k: Konto | 'neu') => {
    setFehler('');
    setForm(k === 'neu' ? { email: '', name: '', rolle: 'verkauf', passwort: '', aktiv: true } : { email: k.email, name: k.name, rolle: k.rolle, passwort: '', aktiv: k.aktiv });
    setBearbeite(k);
  };

  async function speichern() {
    try {
      if (bearbeite === 'neu') {
        await api('/api/benutzer', { method: 'POST', body: form });
      } else if (bearbeite) {
        await api(`/api/benutzer/${bearbeite.id}`, {
          method: 'PATCH',
          body: { name: form.name, rolle: form.rolle, aktiv: form.aktiv, ...(form.passwort ? { passwort: form.passwort } : {}) },
        });
      }
      setBearbeite(null);
      laden();
    } catch (e) {
      setFehler(errorText(e));
    }
  }

  return (
    <>
      <div className="kopf">
        <h1>Benutzer &amp; Rollen</h1>
        <span className="spacer" />
        <button className="primary" onClick={() => oeffne('neu')}>
          + Neuer Benutzer
        </button>
      </div>
      <Card padded={false}>
        {!konten ? (
          <Loading />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Rolle</th>
                <th>Seit</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {konten.map((k) => (
                <tr key={k.id} className="klickbar" onClick={() => oeffne(k)}>
                  <td>
                    <strong>{k.name}</strong>
                  </td>
                  <td>{k.email}</td>
                  <td>{ROLLEN_LABEL[k.rolle]}</td>
                  <td className="small muted">{formatDateTime(k.angelegt_am)}</td>
                  <td>{!k.aktiv && <Badge>deaktiviert</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <p className="small muted">
        Fahrer sehen nur die Kachel Lieferung, Techniker nur Service. Admins verwalten Benutzer und Einstellungen.
      </p>

      {bearbeite && (
        <Dialog
          titel={bearbeite === 'neu' ? 'Neuer Benutzer' : form.name}
          onClose={() => setBearbeite(null)}
          aktionen={
            <>
              <button onClick={() => setBearbeite(null)}>Abbrechen</button>
              <button className="primary" onClick={() => void speichern()}>
                Speichern
              </button>
            </>
          }
        >
          {fehler && <Notice kind="error">{fehler}</Notice>}
          <Field label="Name">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          </Field>
          <Field label="E-Mail (Anmeldename)">
            <input type="email" value={form.email} disabled={bearbeite !== 'neu'} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Rolle">
            <select value={form.rolle} onChange={(e) => setForm({ ...form, rolle: e.target.value as Rolle })}>
              {(Object.keys(ROLLEN_LABEL) as Rolle[]).map((r) => (
                <option key={r} value={r}>
                  {ROLLEN_LABEL[r]}
                </option>
              ))}
            </select>
          </Field>
          <Field label={bearbeite === 'neu' ? 'Passwort (mind. 10 Zeichen)' : 'Neues Passwort (leer lassen = unverändert)'}>
            <input type="password" autoComplete="new-password" value={form.passwort} onChange={(e) => setForm({ ...form, passwort: e.target.value })} />
          </Field>
          {bearbeite !== 'neu' && bearbeite.id !== user?.id && (
            <label className="row">
              <input type="checkbox" style={{ width: 'auto' }} checked={form.aktiv} onChange={(e) => setForm({ ...form, aktiv: e.target.checked })} />
              Zugang aktiv
            </label>
          )}
        </Dialog>
      )}
    </>
  );
}
